import type { FunctionComponent, RefObject } from 'preact';

import {
  createSelectRepeater,
  type Params as SelectParams,
  SelectContextProvider,
} from 'Components/smallComponents/select';
import type { RepeaterFunctionInvoker, TemplateFunction } from 'Components/types';
import { replaceVerifyFitmentVehicleValue, vehicleSelected } from 'Core/actions/fitmentSearch/index';
import { useDispatch } from 'Core/store';
import { type FacetCollection, FacetValue, Vehicle } from 'Models/index';
import type { ServerModel } from 'Modules/serverApi/types';
import { cloneSafe, type CloneSafeProps } from 'Utils/components';
import safeTemplateCall from 'Utils/safeTemplateCall';

export type Params = {
  template: string;
  fields: string[];
  fieldsToSpecify: string;
  selects: RepeaterFunctionInvoker<SelectParams>;
  allowToSetVehicle: boolean;
  vehicleString: string;
  productData: ServerModel.SearchItem;
  selectVehicle: () => void;
  openDialog: () => void;
};

type Props = {
  template: TemplateFunction<Params>;
  rootRef: RefObject<HTMLElement>;
  appendedClasses: CloneSafeProps['appendedClasses'];
  fitmentFields: string[];
  unselectedFacets: FacetCollection;
  vehicleString: string;
  verifyFitmentProductData: ServerModel.SearchItem;
  onSelectVehicle: () => void;
  openDialog: () => void;
};

const VerifyFitmentSpecifier: FunctionComponent<Props> = ({
  template,
  rootRef,
  appendedClasses,
  fitmentFields,
  unselectedFacets,
  vehicleString,
  verifyFitmentProductData,
  onSelectVehicle,
  openDialog,
}) => {
  const dispatch = useDispatch();

  const isAllFieldsSelected = !unselectedFacets.length;

  const selects = createSelectRepeater(
    unselectedFacets
      .filter((f) => f.facetedValues.length > 0)
      .map(({ field, name, facetedValues }, index) => ({
        entries: facetedValues.map((v) => ({
          value: v.value,
          term: v.term,
          payload: v.payload,
          selected: FacetValue.isInCollection(v, unselectedFacets.selection),
        })),
        field,
        extraClassName: 'cm_vehicle-widget_select',
        title: name,
        hideNullOption: true,
        index,
        key: field,
        onChange: (term) => {
          const facetValue = facetedValues.find((v) => v.term === term);
          if (facetValue) {
            dispatch(replaceVerifyFitmentVehicleValue(facetValue, { fitmentFields }));
          }
        },
      })),
  );

  const selectVehicle = () => {
    dispatch(
      vehicleSelected(new Vehicle(unselectedFacets.selection, fitmentFields), {
        isSelectedFromVerifyFitment: true,
        sendIsolatedRequest: true,
      }),
    );
    onSelectVehicle();
  };

  const fieldsToSpecify = unselectedFacets.map((f) => f.name);

  const component = safeTemplateCall(
    template,
    {
      template: 'inexact',
      fields: fieldsToSpecify,
      fieldsToSpecify: fieldsToSpecify.join(', '),
      selects,
      allowToSetVehicle: isAllFieldsSelected,
      vehicleString,
      productData: verifyFitmentProductData,
      selectVehicle,
      openDialog,
    },
    'VerifyFitmentSpecifier',
  );

  return <SelectContextProvider>{cloneSafe(component, rootRef, { appendedClasses })}</SelectContextProvider>;
};

export default VerifyFitmentSpecifier;
